import { Type, Static } from '@sinclair/typebox';
import logger from '@ayocore/platform/common/helpers/logger';

// Performance tracking types
export const PerformanceMetricsSchema = Type.Object({
  renderCount: Type.Number(),
  lastRenderTime: Type.Number(),
  averageRenderTime: Type.Number(),
  memoryUsage: Type.Optional(Type.Number()),
});

export type PerformanceMetrics = Static<typeof PerformanceMetricsSchema>;

// Vanilla JS performance tracker - no React dependencies!
export interface PerformanceTracker {
  trackRender: (metadata?: Record<string, unknown>) => void;
  trackMemory: (label: string) => void;
  trackStateChange: (stateName: string, oldValue: unknown, newValue: unknown) => void;
  startTiming: (operation: string) => void;
  endTiming: (operation: string) => void;
  getMetrics: () => PerformanceMetrics;
  reset: () => void;
}

// Create a stable performance tracker
export function createPerformanceTracker(componentName: string): PerformanceTracker {
  logger.info(`🔗 PERF_TRACKER: Creating vanilla performance tracker for ${componentName}`);

  let renderCount = 0;
  let totalRenderTime = 0;
  let lastRenderStart = 0;
  let timings: Record<string, number> = {};

  return {
    trackRender: (metadata: Record<string, unknown> = {}) => {
      renderCount++;
      const now = performance.now();

      if (lastRenderStart > 0) {
        const renderTime = now - lastRenderStart;
        totalRenderTime += renderTime;

        logger.info(
          `📊 RENDER: ${componentName} render #${renderCount} took ${renderTime.toFixed(2)}ms`,
          metadata
        );
      }

      lastRenderStart = now;
    },

    trackMemory: (label: string) => {
      if ('memory' in performance) {
        type PerfWithMemory = Performance & {
          memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
        };
        const memory = (performance as PerfWithMemory).memory;
        logger.info(`💾 MEMORY: ${componentName} ${label}`, {
          used: memory ? `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB` : 'n/a',
          total: memory ? `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)}MB` : 'n/a',
          limit: memory ? `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)}MB` : 'n/a',
        });
      }
    },

    trackStateChange: (stateName: string, oldValue: unknown, newValue: unknown) => {
      const changed = JSON.stringify(oldValue) !== JSON.stringify(newValue);
      logger.info(`🔄 STATE: ${componentName} ${stateName} changed: ${changed}`, {
        old: oldValue,
        new: newValue,
      });
    },

    startTiming: (operation: string) => {
      timings[operation] = performance.now();
    },

    endTiming: (operation: string) => {
      if (timings[operation]) {
        const duration = performance.now() - timings[operation];
        logger.info(`⏱️ TIMING: ${componentName} ${operation} took ${duration.toFixed(2)}ms`);
        delete timings[operation];
      }
    },

    getMetrics: (): PerformanceMetrics => ({
      renderCount,
      lastRenderTime: totalRenderTime > 0 ? totalRenderTime / renderCount : 0,
      averageRenderTime: totalRenderTime / Math.max(renderCount, 1),
      memoryUsage: (() => {
        if ('memory' in performance) {
          type PerfWithMemory = Performance & { memory?: { usedJSHeapSize: number } };
          const memory = (performance as PerfWithMemory).memory;
          return memory?.usedJSHeapSize;
        }
        return undefined;
      })(),
    }),

    reset: () => {
      renderCount = 0;
      totalRenderTime = 0;
      lastRenderStart = 0;
      timings = {};
      logger.info(`🔄 RESET: ${componentName} performance metrics reset`);
    },
  };
}

// Validation utilities
export function validatePerformanceMetrics(metrics: unknown): metrics is PerformanceMetrics {
  return (
    typeof metrics === 'object' &&
    metrics !== null &&
    typeof (metrics as Record<string, unknown>).renderCount === 'number' &&
    typeof (metrics as Record<string, unknown>).lastRenderTime === 'number' &&
    typeof (metrics as Record<string, unknown>).averageRenderTime === 'number'
  );
}

// Performance monitoring hook for debugging
export function createPerformanceMonitor(componentName: string) {
  const tracker = createPerformanceTracker(componentName);

  return {
    ...tracker,

    // Enhanced logging for debugging
    logRenderCycle: (phase: string, metadata?: Record<string, unknown>) => {
      logger.info(`🔍 RENDER_CYCLE: ${componentName} ${phase}`, metadata);
    },

    // Detect potential infinite loops
    detectInfiniteLoop: (threshold = 100) => {
      const metrics = tracker.getMetrics();
      if (metrics.renderCount > threshold) {
        logger.warn(
          `🚨 INFINITE_LOOP: ${componentName} has rendered ${metrics.renderCount} times!`
        );
        return true;
      }
      return false;
    },

    // Memory leak detection
    detectMemoryLeak: (thresholdMB = 100) => {
      if ('memory' in performance) {
        type PerfWithMemory = Performance & { memory?: { usedJSHeapSize: number } };
        const memory = (performance as PerfWithMemory).memory;
        const usedMB = memory ? memory.usedJSHeapSize / 1024 / 1024 : 0;
        if (memory && usedMB > thresholdMB) {
          logger.warn(`🚨 MEMORY_LEAK: ${componentName} using ${usedMB.toFixed(2)}MB`);
          return true;
        }
      }
      return false;
    },
  };
}
